// Client for the store service (backend/store on :8797, docs/backend/store.md), behind one interface. When the
// service isn't running, pages get the sample store in store-fake.ts and say so.
//
// Package files: the store serves free listed versions' files publicly at `…/versions/<v>/files/<path>`; the preview
// frame reaches them as /api/store-files/<id>/<version>/<path> (serve.ts: a thin anonymous rewrite that also maps
// asset.json). Paid files need a licence, so paid assets show the store's public low-detail model (`preview_glb`) through
// /api/store-preview/<id>/<version>/, and the review queue fetches the one baked model it shows with the moderator's
// session (a signed download link).

import { api, ApiError, isMissingEndpoint } from "./api.ts";
import { findCreator, getCatalogue } from "./catalogue.ts";
import { creatorIdOf, filterAssets } from "./store.ts";
import type { AssetDetail, AssetFilter, AssetSummary, AssetVersion, Check, Manifest, RenderJob, RenderQueue, ReviewItem, ReviewStatus, RuntimeInfo, Stats } from "./store.ts";

export interface CreatorAssets {
  handle: string;
  name?: string;
  creatorId?: string | null;
  assets: AssetSummary[];
}

export interface StoreBackend {
  /** false: the sample store (the service isn't reachable) */
  live: boolean;
  search(f: AssetFilter): Promise<AssetSummary[]>;
  asset(id: string): Promise<AssetDetail | null>;
  creator(handle: string): Promise<CreatorAssets | null>;
  /** the signed-in creator's assets with every version (pending and rejected too) */
  mine(): Promise<AssetDetail[]>;
  yank(id: string, version: string, reason: string): Promise<void>;
  /** moderators only: a 403 means "not a moderator" */
  queue(): Promise<ReviewItem[]>;
  review(id: string, version: string, decision: "approve" | "reject", note: string, opts?: { skipRender?: boolean }): Promise<void>;
  /** can the browser load this version's files without a session (the live 3D preview)? */
  openFiles(a: Pick<AssetSummary, "price">): boolean;
  /** one file of any version the caller may download (the review queue's baked model) */
  fileUrl(id: string, version: string, path: string): Promise<string>;
  /** does the signed-in user hold the developer licence? null = unknown */
  licensed(id: string): Promise<boolean | null>;
  /** the store's newest three.js runtime (GET /v1/runtime); null = unknown */
  runtime(): Promise<RuntimeInfo | null>;
  /** moderators: the render queue (GET /v1/review/jobs); a 403 means "not a moderator" */
  renderQueue(): Promise<RenderQueue>;
  /** moderators: render a version again (POST …/render/retry) */
  retryRender(id: string, version: string): Promise<void>;
}

// ---- the real service -------------------------------------------------------------------------------------------

type Raw = Record<string, unknown>;
const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);
const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const arr = <T = unknown>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const obj = (v: unknown): Raw | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Raw) : null);

/** timestamps arrive as ISO strings (or unix seconds / ms) */
export function secs(v: unknown): number {
  if (typeof v === "number") return v > 1e12 ? Math.floor(v / 1000) : v;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isFinite(t) ? Math.floor(t / 1000) : 0;
  }
  return 0;
}

const enc = (id: string) => id.split("/").map(encodeURIComponent).join("/");
/** The store builds absolute URLs on its own origin; the page reaches it through the site's /api/store proxy. */
export function viaProxy(u: string | null | undefined): string | null {
  if (!u) return null;
  try {
    const url = new URL(u, "http://x");
    return url.pathname.startsWith("/v1/") ? `/api/store${url.pathname}${url.search}` : null;
  } catch {
    return null;
  }
}
export const openFilesBase = (id: string, version: string): string => `/api/store-files/${enc(id)}/${encodeURIComponent(version)}/`;
/** The site's route to a version's public preview model (serve.ts): preview.glb and a code-free asset.json. */
export const previewModelBase = (id: string, version: string): string => `/api/store-preview/${enc(id)}/${encodeURIComponent(version)}/`;

/** The creator's param order: the signed manifest's `params.order`, else the view's `params_order`. */
export function withParamsOrder(m: Manifest | null, order: unknown): Manifest | null {
  if (!m || !m.params || Array.isArray(m.params.order) || !Array.isArray(order)) return m;
  return { ...m, params: { ...m.params, order: order.filter((k): k is string => typeof k === "string") } };
}

const JOB_STATES = new Set(["queued", "running", "failed", "done"]);
/** A render job from `render_job` on an insider version view, or from GET /v1/review/jobs. */
export function normJob(r: unknown): RenderJob | null {
  const j = obj(r);
  if (!j) return null;
  const state = str(j.state);
  if (!JOB_STATES.has(state)) return null;
  return {
    state: state as RenderJob["state"],
    attempts: num(j.attempts) ?? 0,
    maxAttempts: num(j.max_attempts),
    worker: str(j.worker) || null,
    error: str(j.error) || null,
    queuedAt: secs(j.queued_at) || null,
    leaseUntil: secs(j.lease_until) || null,
    retryAfter: secs(j.retry_after) || null,
  };
}
const previewOf = (id: string, version: string) => `/api/store/v1/packages/${enc(id)}/versions/${encodeURIComponent(version)}/preview.png`;

function status(v: unknown): ReviewStatus {
  const s = str(v);
  return s === "listed" || s === "rejected" || s === "yanked" || s === "uploading" ? s : "pending";
}

/** The service's `checks` ({ok, problems, warnings, stats}) and `render` report → one list for people. */
export function checksOf(checks: unknown, render: unknown): Check[] {
  const out: Check[] = [];
  const c = obj(checks);
  if (c) {
    const problems = arr<string>(c.problems);
    const warnings = arr<string>(c.warnings);
    if (c.ok === true && !problems.length) out.push({ name: "package", result: "pass", detail: "manifest, file hashes, licence file, budget, rig and code scan" });
    else if (c.ok === false && !problems.length) out.push({ name: "package", result: "fail" });
    for (const p of problems) out.push({ name: "package", result: "fail", detail: p });
    for (const w of warnings) out.push({ name: "package", result: "warn", detail: w });
  }
  const r = obj(render);
  if (!r) out.push({ name: "render", result: "skip", detail: "not run yet (the render reviewer: bun store/src/review.js)" });
  else if (r.skipped) out.push({ name: "render", result: "warn", detail: str(r.note, "skipped by a moderator") });
  else if (r.ok === true) {
    out.push({ name: "render", result: "pass", detail: renderSummary(r) });
  } else {
    const problems = arr<string>(r.problems);
    if (!problems.length) out.push({ name: "render", result: "fail" });
    for (const p of problems) out.push({ name: "render", result: "fail", detail: p });
  }
  return out;
}

/** A passing render report in words: how much of the frame it fills, whether it repeats, whether it cleans up. */
export function renderSummary(r: Raw): string {
  const bits: string[] = [];
  const cov = num(r.coverage) ?? num(obj(r.coverage)?.fraction);
  if (cov !== undefined) bits.push(`fills ${Math.round(cov * 100)}% of the frame`);
  const det = obj(r.determinism);
  if (det && typeof det.same === "boolean") bits.push(det.same ? "two renders identical" : "renders differ");
  const leaked = obj(obj(r.dispose)?.leaked);
  if (leaked) bits.push(Object.values(leaked).every((v) => v === 0) ? "nothing left after dispose" : `left after dispose: ${JSON.stringify(leaked)}`);
  const phys = obj(r.physics);
  if (phys && typeof phys.ok === "boolean") bits.push(phys.ok ? "physics drop settles" : "physics drop failed");
  return bits.join(", ") || "renders, repeats and cleans up";
}

function statsOf(r: Raw): Stats | undefined {
  const c = obj(r.checks);
  const st = obj(c?.stats);
  const out: Stats = {};
  const t = num(r.triangles) ?? num(st?.triangles);
  const d = num(r.drawCalls) ?? num(st?.drawCalls);
  const b = num(r.bytes);
  if (t !== undefined) out.triangles = t;
  if (d !== undefined) out.drawCalls = d;
  if (b !== undefined) out.bytes = b;
  return Object.keys(out).length ? out : undefined;
}

let creatorNames: Promise<Map<string, string>> | null = null;
function names(): Promise<Map<string, string>> {
  creatorNames ??= getCatalogue().then(
    (c) => new Map(c.creators.map((x) => [x.id, x.name])),
    () => new Map(),
  );
  return creatorNames;
}

/** A search result or package summary from the service. */
export function normSummary(r: Raw, nameOf: Map<string, string> = new Map()): AssetSummary {
  const m = obj(r.manifest) as Partial<Manifest> | null;
  const id = str(r.id ?? m?.id);
  const handle = id.split("/")[0] ?? "";
  const creatorId = str(r.creator).startsWith("cr_") ? str(r.creator) : creatorIdOf(handle);
  const version = str(r.version ?? m?.version);
  return {
    id,
    title: str(r.title ?? m?.title, id),
    kind: str(r.kind ?? m?.kind, "model"),
    tags: arr<string>(r.tags ?? m?.tags),
    conventions: arr<string>(r.conventions ?? m?.conventions),
    creator: { handle, name: nameOf.get(creatorId), verified: true, creatorId },
    price: num(r.price ?? m?.price) ?? 0,
    licence: str(r.licence ?? m?.licence?.tier, "commercial"),
    cosmetic: (r.cosmetic ?? m?.cosmetic) === true,
    version,
    previewUrl: viaProxy(str(r.preview)) ?? (id && version ? previewOf(id, version) : null),
    stats: statsOf(r),
    updatedAt: secs(r.updated_at ?? r.updatedAt),
  };
}

/** One version view from the service. */
export function normVersion(id: string, r: Raw): AssetVersion {
  const version = str(r.version);
  const yanked = obj(r.yanked);
  return {
    version,
    status: status(r.status),
    publishedAt: secs(r.submitted_at ?? r.created_at),
    note: str(r.note) || null,
    yankReason: str(yanked?.reason) || null,
    checks: checksOf(r.checks, r.render),
    signed: !!obj(r.signature),
    stats: statsOf(r),
    base: openFilesBase(id, version),
    manifest: withParamsOrder(obj(r.manifest) as Manifest | null, r.params_order) ?? undefined,
    renderJob: normJob(r.render_job),
  };
}

const semverCmp = (a: string, b: string): number => {
  const pa = a.split(/[.-]/).map((x) => (/^\d+$/.test(x) ? Number(x) : x));
  const pb = b.split(/[.-]/).map((x) => (/^\d+$/.test(x) ? Number(x) : x));
  for (let i = 0; i < 3; i++) if (pa[i] !== pb[i]) return Number(pa[i]) - Number(pb[i]);
  return a.length - b.length || a.localeCompare(b);
};
export { semverCmp };

function sortBy(list: AssetSummary[], f: AssetFilter): AssetSummary[] {
  return f.sort && f.sort !== "new" ? filterAssets(list, { sort: f.sort }) : list;
}

export const liveStore: StoreBackend = {
  live: true,
  async search(f) {
    const q = new URLSearchParams();
    if (f.q?.trim()) q.set("q", f.q.trim());
    if (f.kind) q.set("kind", f.kind);
    if (f.convention) q.set("convention", f.convention);
    if (f.price) q.set("free", f.price === "free" ? "1" : "0");
    q.set("limit", "100");
    const [r, n] = await Promise.all([api<{ results?: Raw[] }>("store", `/search?${q}`, { auth: false }), names()]);
    return sortBy(arr<Raw>(r.results).map((x) => normSummary(x, n)), f);
  },
  async asset(id) {
    let p: Raw;
    try {
      p = await api<Raw>("store", `/packages/${enc(id)}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404 && !isMissingEndpoint(e)) return null;
      throw e;
    }
    if (!p.version) return null; // nothing listed (every version yanked)
    const [v, n] = await Promise.all([api<Raw>("store", `/packages/${enc(id)}/versions/${encodeURIComponent(str(p.version))}`), names()]);
    const sum = normSummary(p, n);
    const manifest = withParamsOrder(obj(v.manifest) as Manifest, v.params_order) as Manifest;
    return {
      ...sum,
      stats: { ...sum.stats, ...statsOf(v) },
      manifest,
      base: openFilesBase(id, sum.version),
      previewModel: str(v.preview_glb) ? previewModelBase(id, sum.version) : null,
      versions: arr<Raw>(p.versions).map((x) => normVersion(id, x)),
    };
  },
  async creator(handle) {
    const [r, n] = await Promise.all([api<{ creator?: string; packages?: Raw[] }>("store", `/creators/${encodeURIComponent(handle)}/packages`, { auth: false }), names()]);
    const creatorId = str(r.creator, creatorIdOf(handle));
    const assets = arr<Raw>(r.packages).map((x) => normSummary(x, n)).filter((a) => a.version);
    if (n.has(creatorId)) return { handle, name: n.get(creatorId), creatorId, assets };
    // no game: a creator with just a handle (accounts wave 6) has a page and a name, with or without assets
    const found = await findCreator(creatorId).catch(() => null);
    if (!assets.length && !found) return null;
    return { handle, name: found?.creator.name, creatorId, assets };
  },
  async mine() {
    const [r, n] = await Promise.all([api<{ versions?: Raw[] }>("store", "/me/packages"), names()]);
    const groups = new Map<string, Raw[]>();
    for (const v of arr<Raw>(r.versions)) {
      const id = str(v.id);
      groups.set(id, [...(groups.get(id) ?? []), v]);
    }
    const out: AssetDetail[] = [];
    for (const [id, vs] of groups) {
      vs.sort((a, b) => semverCmp(str(b.version), str(a.version)));
      const versions = vs.map((v) => normVersion(id, v));
      const top = vs.find((v) => v.status === "listed") ?? vs[0]!;
      const sum = normSummary({ ...top, preview: top.preview ?? null }, n);
      const listed = versions.find((v) => v.status === "listed");
      out.push({
        ...sum,
        previewUrl: listed ? previewOf(id, listed.version) : null,
        manifest: { id, version: sum.version, title: sum.title, kind: sum.kind, author: { handle: sum.creator.handle }, licence: { tier: sum.licence } },
        base: openFilesBase(id, sum.version),
        versions,
      });
    }
    return out.sort((a, b) => a.title.localeCompare(b.title));
  },
  async yank(id, version, reason) {
    await api("store", `/packages/${enc(id)}/versions/${encodeURIComponent(version)}/yank`, { body: { reason } });
  },
  async queue() {
    const [r, n] = await Promise.all([api<{ versions?: Raw[] }>("store", "/review/queue"), names()]);
    const vs = arr<Raw>(r.versions);
    const ids = [...new Set(vs.map((v) => str(v.id)))];
    // the previous public version of each package, for the manifest diff
    const previous = new Map<string, Raw[]>();
    await Promise.all(
      ids.map(async (id) => {
        const p = await api<Raw>("store", `/packages/${enc(id)}`).catch(() => null);
        previous.set(id, arr<Raw>(p?.versions).filter((v) => v.status === "listed" || v.status === "yanked"));
      }),
    );
    return Promise.all(
      vs.map(async (v): Promise<ReviewItem> => {
        const id = str(v.id);
        const version = str(v.version);
        const m = (obj(v.manifest) ?? {}) as Manifest;
        const prior = (previous.get(id) ?? []).filter((p) => semverCmp(str(p.version), version) < 0).sort((a, b) => semverCmp(str(b.version), str(a.version)))[0];
        const prev = prior ? await api<Raw>("store", `/packages/${enc(id)}/versions/${encodeURIComponent(str(prior.version))}`).catch(() => null) : null;
        return {
          id,
          version,
          title: str(v.title ?? m.title, id),
          kind: str(v.kind ?? m.kind),
          creator: { handle: id.split("/")[0] ?? "", name: n.get(creatorIdOf(id.split("/")[0] ?? "")) },
          submittedAt: secs(v.submitted_at ?? v.created_at),
          checks: checksOf(v.checks, v.render),
          manifest: m,
          previous: prev && obj(prev.manifest) ? { version: str(prev.version), manifest: prev.manifest as Manifest } : null,
          base: openFilesBase(id, version),
          previewUrl: null, // unlisted previews aren't public; the card loads the baked model with the moderator's session
          stats: statsOf(v),
          hasCode: !!m.engines && Object.keys(m.engines).length > 0,
          renderMissing: !obj(v.render),
          renderJob: normJob(v.render_job),
        };
      }),
    );
  },
  async review(id, version, decision, note, opts = {}) {
    await api("store", `/packages/${enc(id)}/versions/${encodeURIComponent(version)}/review`, { body: { decision, note, ...(opts.skipRender ? { skip_render: true } : {}) } });
  },
  openFiles(a) {
    return a.price === 0;
  },
  async fileUrl(id, version, path) {
    const r = await api<{ files?: { path: string; url: string }[] }>("store", `/packages/${enc(id)}/versions/${encodeURIComponent(version)}/download`, { body: {} });
    const f = arr<{ path: string; url: string }>(r.files).find((x) => x.path === path);
    const u = viaProxy(f?.url);
    if (!u) throw new Error(`${path} isn't in ${id}@${version}`);
    return u;
  },
  async licensed(id) {
    try {
      const r = await api<{ licensed?: boolean }>("store", `/packages/${enc(id)}/licence`);
      return r.licensed === true;
    } catch {
      return null;
    }
  },
  runtime() {
    runtimeInfo ??= api<Raw>("store", "/runtime", { auth: false }).then(runtimeOf, () => null);
    return runtimeInfo;
  },
  async renderQueue() {
    const r = await api<Raw>("store", "/review/jobs");
    return renderQueueOf(r);
  },
  async retryRender(id, version) {
    await api("store", `/packages/${enc(id)}/versions/${encodeURIComponent(version)}/render/retry`, { body: {} });
  },
};

let runtimeInfo: Promise<RuntimeInfo | null> | null = null;

/** GET /v1/runtime → the newest three.js runtime: `{three: {latest, versions: {"1.0.0": {url, …}}}}`. */
export function runtimeOf(r: unknown): RuntimeInfo | null {
  const three = obj(obj(r)?.three);
  const latest = str(three?.latest);
  const url = str(obj(obj(three?.versions)?.[latest])?.url);
  return latest && /^https?:\/\//.test(url) ? { version: latest, url } : null;
}

/** GET /v1/review/jobs → counts per state and the jobs, by version ref. */
export function renderQueueOf(r: unknown): RenderQueue {
  const o = obj(r) ?? {};
  const c = obj(o.counts) ?? {};
  const jobs = new Map<string, RenderJob>();
  for (const j of arr<Raw>(o.jobs)) {
    const job = normJob({ ...j, max_attempts: j.max_attempts ?? o.max_attempts });
    const ref = str(j.ref);
    if (job && ref) jobs.set(ref, job);
  }
  return { counts: { queued: num(c.queued) ?? 0, running: num(c.running) ?? 0, failed: num(c.failed) ?? 0, done: num(c.done) ?? 0 }, jobs };
}

// ---- choosing ---------------------------------------------------------------------------------------------------

let chosen: Promise<StoreBackend> | null = null;

/**
 * The sample store (store-fake.ts) is opt-in for offline page work only: `?sample-store=1` once, or
 * localStorage `thrdm.sampleStore = "1"`. Otherwise the pages always talk to the real service, and an outage shows as
 * an error: dashboards must never offer yank or approve on made-up data.
 */
export function sampleStoreWanted(search = typeof location === "undefined" ? "" : location.search): boolean {
  try {
    const q = new URLSearchParams(search).get("sample-store");
    if (q === "1") localStorage.setItem("thrdm.sampleStore", "1");
    if (q === "0") localStorage.removeItem("thrdm.sampleStore");
    return localStorage.getItem("thrdm.sampleStore") === "1";
  } catch {
    return new URLSearchParams(search).get("sample-store") === "1";
  }
}

/** The real store service, or the sample store when asked for (loaded only then). */
export function store(): Promise<StoreBackend> {
  chosen ??= sampleStoreWanted() ? import("./store-fake.ts").then((m) => m.fakeStore) : Promise.resolve(liveStore);
  return chosen;
}

/** Tests and the dev page can pin a backend. */
export function useStore(b: StoreBackend | null): void {
  chosen = b ? Promise.resolve(b) : null;
}

// ---- licences (money) -------------------------------------------------------------------------------------------

let ownedCache: { key: string; ids: Promise<Set<string> | null> } | null = null;

/**
 * Asset licences the signed-in buyer holds: money's `GET /v1/me/inventory?kind=asset` (item ids `a_<creator>/<name>`),
 * one call for a whole grid. A licence is not a cosmetic: wearing is `kind=cosmetic` and doesn't count here. null =
 * unknown (signed out or money unreachable).
 */
export function ownedAssets(userId: string | null): Promise<Set<string> | null> {
  if (!userId) return Promise.resolve(null);
  if (ownedCache?.key !== userId) {
    ownedCache = {
      key: userId,
      ids: api<{ items: { item_id: string; kind?: string }[] }>("money", "/me/inventory?kind=asset").then(
        (r) => new Set(r.items.filter((i) => !i.kind || i.kind === "asset").map((i) => i.item_id)),
        () => null,
      ),
    };
  }
  return ownedCache.ids;
}
